import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Stack, Typography } from '../../../ui/core';
import { pillButtonSx } from '../../../ui/styles';
import LanePolylineApi from '../../../lib/api/LanePolylineApi';

/**
 * Past edits, newest first.
 *
 * Revert does NOT write. It loads the entry's previous geometry as the
 * current candidate and drops the editor into REVIEW, so a revert goes
 * through exactly the same impact preview and reason note as any other save.
 * A revert is still a geometry change with the same blast radius — the fact
 * that the geometry is old does not make it safe, since the stations around
 * it may have changed since.
 *
 * An entry with no `previousPolylineGeoJson` is the lane's first-ever edit,
 * made when it had no geometry at all. There is nothing to revert to.
 */
const HistoryPanel = ({ laneId, onRevert, canRevert = true }) => {
  const { data: history, isLoading, isError } = useQuery({
    queryKey: ['lane-polyline-history', laneId],
    queryFn: () => LanePolylineApi.fetchHistory(laneId),
    enabled: !!laneId,
  });

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading history…
      </Typography>
    );
  }

  if (isError) {
    return <Alert severity="error">Could not load this lane&apos;s edit history.</Alert>;
  }

  if (!history || history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No edits yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">History</Typography>
      {history.map((entry) => (
        <Stack key={entry.id} spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {`${new Date(entry.createdAt).toLocaleString()} · ${entry.sourceAdminUser}`}
          </Typography>
          <Typography variant="body2">{entry.reason}</Typography>
          {entry.previousPolylineGeoJson && canRevert && (
            <Button
              sx={pillButtonSx}
              onClick={() => onRevert(entry.previousPolylineGeoJson)}
            >
              Revert to this
            </Button>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export default HistoryPanel;
